import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShareIcon,
  EyeIcon,
  RocketLaunchIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

interface WorkflowWithPermission {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  workflowContainerId: number;
  workflowContainerName: string;
  canStartWorkflow: boolean;
  nodes?: any[];
  edges?: any[];
  nodesCount?: number;
  edgesCount?: number;
}

export const EmployeeWorkflows: React.FC = () => {
  const { t } = useTranslation(["workflows", "common", "errors", "projects"]);
  const navigate = useNavigate();
  const { getWorkflowLabel, getWorkflowsLabel, hasWorkflowTerminologyOverride } = useAuth();

  const [workflows, setWorkflows] = useState<WorkflowWithPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedWorkflowForStart, setSelectedWorkflowForStart] =
    useState<WorkflowWithPermission | null>(null);

  const workflowText = getWorkflowLabel();
  const workflowsText = getWorkflowsLabel();
  const workflowTextLower = workflowText.toLocaleLowerCase("tr-TR");
  const workflowsTextLower = workflowsText.toLocaleLowerCase("tr-TR");
  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const workflowNameText = useWorkflowOverride
    ? `${workflowText} Adı`
    : t("workflows:workflowName");
  const unnamedWorkflowText = useWorkflowOverride
    ? `İsimsiz ${workflowText}`
    : t("workflows:unnamedWorkflow");
  const noAccessibleWorkflowsText = useWorkflowOverride
    ? `Henüz erişilebilir ${workflowTextLower} yok`
    : t("workflows:noAccessibleWorkflows");
  const noAccessibleWorkflowsDescriptionText = useWorkflowOverride
    ? `Proje yöneticiniz sizi bir projeye eklediğinde ${workflowsTextLower} burada görebileceksiniz.`
    : t("workflows:noAccessibleWorkflowsDescription");
  const workflowAutoAppearHintText = useWorkflowOverride
    ? `Bir projeye dahil olduğunuzda ${workflowsTextLower} otomatik olarak burada görünecektir`
    : t("workflows:workflowAutoAppearHint");
  const workflowInstanceCreatedText = useWorkflowOverride
    ? `${workflowText} başarıyla başlatıldı`
    : t("workflows:workflowInstanceCreated");
  const loadingWorkflowsText = useWorkflowOverride
    ? `${workflowsText} yükleniyor...`
    : t("workflows:loadingWorkflows");

  useEffect(() => {
    loadAccessibleWorkflows();
  }, []);

  const loadAccessibleWorkflows = async () => {
    try {
      const data = await apiService.getAccessibleWorkflows();
      setWorkflows(data);
    } catch (error) {
      toast.error(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleStartWorkflow = (workflow: WorkflowWithPermission) => {
    setSelectedWorkflowForStart(workflow);
    setShowStartModal(true);
  };

  const handleWorkflowStartSuccess = () => {
    setShowStartModal(false);
    setSelectedWorkflowForStart(null);
    toast.success(workflowInstanceCreatedText);
    loadAccessibleWorkflows(); // Refresh
  };

  const handleViewWorkflow = (workflowId: number) => {
    navigate(`/workflows/${workflowId}/view`);
  };

  if (loading) {
    return (
      <div className="app-page pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-slate-200 border-t-blue-600 mx-auto mb-4"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-400 animate-pulse"></div>
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {loadingWorkflowsText}
          </h3>
          <p className="text-slate-500">{t("common:pleaseWait")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page pt-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-4">
        {/* Workflows List - Compact Horizontal View */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {/* Table Header - Hidden on Mobile */}
          <div className="hidden lg:block bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-slate-200 px-6 py-3">
            <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              <div className="col-span-6">{workflowNameText}</div>
              <div className="col-span-2">{t("workflows:nodes")}</div>
              <div className="col-span-2">{t("workflows:permission")}</div>
              <div className="col-span-2 text-right">{t("common:actions")}</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {workflows && workflows.length > 0
              ? workflows.map((workflow) => (
                  <div
                    key={workflow.id}
                    className="relative group hover:bg-blue-50/30 transition-all duration-200"
                  >
                    {/* Workflow Row */}
                    <div className="px-4 lg:px-6 py-4">
                      {/* Desktop Layout (lg+) */}
                      <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                        {/* Workflow Name */}
                        <div className="col-span-6">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                                <CubeIcon className="w-5 h-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-slate-800 truncate group-hover:text-blue-700 transition-colors">
                                {workflow.name ||
                                  unnamedWorkflowText}
                              </h3>
                            </div>
                          </div>
                        </div>

                        {/* Nodes/Edges */}
                        <div className="col-span-2">
                          <div className="text-xs space-y-1">
                            <div className="flex items-center text-slate-600">
                              <span className="font-medium mr-1">
                                {t("workflows:nodes")}:
                              </span>
                              <span className="text-slate-800 font-semibold">
                                {workflow.nodesCount ??
                                  workflow.nodes?.length ??
                                  0}
                              </span>
                            </div>
                            <div className="flex items-center text-slate-600">
                              <span className="font-medium mr-1">
                                {t("workflows:edges")}:
                              </span>
                              <span className="text-slate-800 font-semibold">
                                {workflow.edgesCount ??
                                  workflow.edges?.length ??
                                  0}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Permission Badge */}
                        <div className="col-span-2">
                          {workflow.canStartWorkflow ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              ✓ {t("workflows:canStart")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              {t("workflows:viewOnly")}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="col-span-2">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewWorkflow(workflow.id)}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold"
                              title={t("common:view")}
                            >
                              <EyeIcon className="w-3.5 h-3.5" />
                              <span>{t("common:view")}</span>
                            </button>

                            {workflow.canStartWorkflow && (
                              <button
                                onClick={() => handleStartWorkflow(workflow)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold"
                                title={t("workflows:start")}
                              >
                                <RocketLaunchIcon className="w-3.5 h-3.5" />
                                <span>{t("workflows:start")}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Mobile & Tablet Layout */}
                      <div className="lg:hidden space-y-3">
                        {/* Header Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
                              <CubeIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-slate-800 truncate">
                                {workflow.name ||
                                  unnamedWorkflowText}
                              </h3>
                            </div>
                          </div>
                          {workflow.canStartWorkflow ? (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 bg-green-100 text-green-700">
                              ✓ {t("workflows:canStart")}
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 bg-gray-100 text-gray-600">
                              {t("common:view")}
                            </span>
                          )}
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500 font-medium">
                              {t("workflows:nodes")}
                            </span>
                            <p className="text-slate-800 font-semibold">
                              {workflow.nodes?.length || 0}
                            </p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">
                              {t("workflows:edges")}
                            </span>
                            <p className="text-slate-800 font-semibold">
                              {workflow.edges?.length || 0}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleViewWorkflow(workflow.id)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-xs font-semibold"
                          >
                            <EyeIcon className="w-3.5 h-3.5" />
                            <span>{t("common:view")}</span>
                          </button>

                          {workflow.canStartWorkflow && (
                            <button
                              onClick={() => handleStartWorkflow(workflow)}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-xs font-semibold"
                            >
                              <RocketLaunchIcon className="w-3.5 h-3.5" />
                              <span>{t("workflows:start")}</span>
                            </button>
                          )}
                        </div>

                        {/* Date */}
                        <div className="text-xs text-slate-500">
                          {t("dashboard:createdAt")}:{" "}
                          {new Date(workflow.createdAt).toLocaleDateString(
                            "tr-TR"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : null}
          </div>
        </div>

        {/* Empty State */}
        {workflows.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-12 border border-white/20 shadow-xl max-w-lg mx-auto">
              <ShareIcon className="w-24 h-24 text-slate-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-slate-700 mb-4">
                {noAccessibleWorkflowsText}
              </h3>
              <p className="text-slate-500 mb-8 leading-relaxed">
                {noAccessibleWorkflowsDescriptionText}
              </p>
              <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-700 text-sm">
                  💡 {workflowAutoAppearHintText}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Workflow Start Modal */}
      {selectedWorkflowForStart && (
        <WorkflowStartModal
          isOpen={showStartModal}
          onClose={() => {
            setShowStartModal(false);
            setSelectedWorkflowForStart(null);
          }}
          projectId={selectedWorkflowForStart.workflowContainerId}
          workflowId={selectedWorkflowForStart.id}
          onSuccess={handleWorkflowStartSuccess}
        />
      )}
    </div>
  );
};

